import {
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Stack,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from "@mui/material";
import { InputBaseComponentProps } from "@mui/material/InputBase/InputBase";
import CloseEyeIcon from "assets/icons/CloseEyeIcon";
import OpenEyeIcon from "assets/icons/OpenEyeIcon";
import TriangleArrow from "assets/icons/TriangleArrow";
import { LabelLink } from "components/FieldLabel/types";
import { forwardRef, ReactNode, useState } from "react";
import { FieldError } from "react-hook-form";
import { IMask, IMaskInput } from "react-imask";
import FieldLabel from "../FieldLabel";
import getInputVariantSx from "./inputStyles";

export type TextFieldProps = {
  endIcon?: ReactNode;
  handleChange?: (value: string, onChange: (newValue: string) => void) => void;
  label?: ReactNode;
  labelLink?: LabelLink;
  labelTypographyProps?: TypographyProps;
  maskType?: "time";
  maskProps?: { mask: string };
  startIcon?: ReactNode | string;
  onNumberValueChange?: (value: number) => void;
  hideHelperText?: boolean;
  stackSx?: SxProps<Theme>;
  error?: FieldError;
  onChange: (value: string) => void;
  required?: boolean;
  horizontalMode?: boolean;
  disabledViewAsText?: boolean;
  hint?: string;
  endAction?: ReactNode;
} & Omit<MuiTextFieldProps, "label" | "name" | "onChange" | "error">;

// eslint-disable-next-line react/display-name
const MaskCustom = forwardRef<HTMLInputElement, InputBaseComponentProps>((props, ref) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      inputRef={ref}
      onAccept={(value) => {
        onChange?.({ target: { name: props.name, value } });
      }}
      unmask
    />
  );
});

const TextField = ({
  value,
  onChange,
  placeholder,
  variant,
  size,
  onNumberValueChange,
  handleChange,
  label,
  labelLink,
  labelTypographyProps,
  type,
  maskProps,
  startIcon,
  hideHelperText,
  hint,
  stackSx,
  error,
  disabled,
  required,
  horizontalMode = false,
  disabledViewAsText = false,
  InputProps,
  sx: textFieldSx,
  endAction,
  ...props
}: TextFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";

  const isTimeInput = props.maskType === "time";

  const generateInputProps = () => {
    if (isTimeInput) {
      return {
        mask: "HH:MM",
        blocks: {
          HH: {
            mask: IMask.MaskedRange,
            placeholderChar: "HH",
            from: 0,
            to: 23,
            maxLength: 2,
          },
          MM: {
            mask: IMask.MaskedRange,
            placeholderChar: "MM",
            from: 0,
            to: 59,
            maxLength: 2,
          },
        },
      };
    }
    if (maskProps) return maskProps;
    return {};
  };

  const inputProps = generateInputProps();

  const getInputType = () => {
    if (isPassword) {
      if (!showPassword) return "password";
      return "text";
    }
    return type;
  };

  const inputType = getInputType();

  const handleChangeMinutes = (action: "up" | "down") => {
    if (inputType !== "number") return;

    const currentValueString = value as string;

    const currentValue = currentValueString ? parseInt(currentValueString, 10) : 0;

    let newValue = currentValue;
    if (action === "up") {
      newValue = currentValue + 1;
    } else {
      newValue = currentValue - 1;
    }
    if (onNumberValueChange) {
      onNumberValueChange(newValue);
    } else if (onChange) onChange(String(newValue));
  };

  const generateEndIcon = () => {
    if (isPassword) {
      return (
        <IconButton
          onClick={() => setShowPassword(!showPassword)}
          edge='end'>
          {showPassword ? <OpenEyeIcon sx={{ color: "base.200" }} /> : <CloseEyeIcon sx={{ color: "base.200" }} />}
        </IconButton>
      );
    }
    if (inputType === "number") {
      return (
        <Stack>
          <TriangleArrow
            onClick={disabled ? undefined : () => handleChangeMinutes("up")}
            sx={{
              color: "base.200",
              width: 14,
              height: 14,
              cursor: "pointer",
            }}
          />
          <TriangleArrow
            onClick={disabled ? undefined : () => handleChangeMinutes("down")}
            sx={{
              color: "base.200",
              transform: "rotate(180deg)",
              width: 14,
              height: 14,
              cursor: "pointer",
            }}
          />
        </Stack>
      );
    }
    if (props.endIcon) return props.endIcon;
    return undefined;
  };
  const endIcon = generateEndIcon();

  const getPlaceholder = () => {
    if (typeof placeholder !== "undefined") {
      return placeholder;
    }
    if (typeof label === "string") {
      return label;
    }
    return "";
  };

  return (
    <Stack
      spacing={0.5}
      sx={{ width: props.fullWidth ? "100%" : undefined, ...stackSx }}
      {...(horizontalMode && { direction: "row", alignItems: "baseline" })}>
      {label && (
        <FieldLabel
          label={label}
          disabled={disabled}
          required={required}
          labelLink={labelLink}
          horizontalMode={horizontalMode}
          typographyProps={labelTypographyProps}
        />
      )}
      <Stack
        spacing={1.5}
        useFlexGap
        direction='row'>
        {disabled && disabledViewAsText ? (
          <Typography sx={{ ...InputProps?.sx }}>{value as ReactNode}</Typography>
        ) : (
          <MuiTextField
            {...props}
            disabled={disabled}
            value={value}
            onChange={(e) => {
              const { value: newValue } = e.target;
              if (handleChange) handleChange(newValue, onChange);
              else onChange(newValue);
            }}
            size={size}
            error={!!error}
            fullWidth
            placeholder={getPlaceholder()}
            type={inputType}
            sx={getInputVariantSx(
              variant,
              {
                '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button':
                  {
                    display: "none",
                    WebkitAppearance: "none",
                    margin: 0,
                  },
              },
              textFieldSx
            )}
            variant={variant}
            helperText={!hideHelperText ? error?.message || hint : undefined}
            InputProps={{
              endAdornment: endIcon ? <InputAdornment position='end'>{endIcon}</InputAdornment> : undefined,
              startAdornment: startIcon ? <InputAdornment position='start'>{startIcon}</InputAdornment> : undefined,
              inputProps,
              inputComponent: maskProps?.mask || isTimeInput ? MaskCustom : undefined,
              ...InputProps,
            }}
          />
        )}
        {endAction}
      </Stack>
    </Stack>
  );
};

export default TextField;
